import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Instagram, ExternalLink } from 'lucide-react';

const InstagramFeed = () => {
  const [embedsLoaded, setEmbedsLoaded] = useState(false);

  // Instagram post URLs for @shoppersdiscountclub
  // These will be updated with your actual recent post URLs from the admin panel
  const instagramPosts = [
    'https://www.instagram.com/p/DPHRPlmkyU6/?igsh=MXRqNzc4aThjMzJlOQ==',
    'https://www.instagram.com/p/DPHXb1Aj8s3/?igsh=eGtlbHpudHQ1bXZk',
    'https://www.instagram.com/p/DPHZe4WjaV6/?igsh=aDBjdW5nNmJ0ZWNk',
    'https://www.instagram.com/reel/Cp-TR6Jg5Mi/?igsh=MWwzYjg3N3FvMDNsNA=='
  ];

  useEffect(() => {
    // Load Instagram embed script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.instagram.com/embed.js';
    script.onload = () => {
      setEmbedsLoaded(true);
      // Process embeds after script loads
      if (window.instgrm) {
        window.instgrm.Embeds.process();
      }
    };
    document.body.appendChild(script);

    // Auto-refresh embeds every 5 minutes
    const refreshInterval = setInterval(() => {
      if (window.instgrm) {
        window.instgrm.Embeds.process();
      }
    }, 300000); // 5 minutes

    return () => {
      clearInterval(refreshInterval);
      // Clean up script
      const scripts = document.querySelectorAll('script[src="https://www.instagram.com/embed.js"]');
      scripts.forEach(script => script.remove());
    };
  }, []);

  const InstagramEmbed = ({ postUrl, index }) => {
    return (
      <div className="instagram-embed-container bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
        <blockquote 
          className="instagram-media" 
          data-instgrm-captioned 
          data-instgrm-permalink={postUrl}
          data-instgrm-version="14"
          style={{
            background: '#FFF',
            border: '0',
            borderRadius: '8px',
            boxShadow: '0 0 1px 0 rgba(0,0,0,0.5), 0 1px 10px 0 rgba(0,0,0,0.15)',
            margin: '1px',
            maxWidth: '100%',
            minWidth: '326px',
            padding: '0',
            width: '100%'
          }}
        >
          <div style={{ padding: '16px' }}>
            <div className="flex items-center justify-center h-48 bg-gray-100 rounded-lg">
              <div className="text-center">
                <Instagram className="h-12 w-12 text-primary mx-auto mb-4" />
                <p className="text-gray-600 text-sm">
                  Loading Instagram post...
                </p>
                <a 
                  href={postUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-orange transition-colors text-sm inline-flex items-center gap-1 mt-2"
                >
                  View on Instagram <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
        </blockquote>
      </div>
    );
  };

  return (
    <section className="py-16 bg-gradient-to-br from-primary/5 via-white to-orange/5">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Instagram className="h-8 w-8 text-primary" />
            <h2 className="text-3xl lg:text-4xl font-bold text-charcoal">
              From Our Instagram @shoppersdiscountclub
            </h2>
          </div>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Follow us for the latest deals, job opportunities, events, and insider shopping tips from across the UK
          </p>
        </div>

        {/* Desktop: 2x2 Grid */}
        <div className="hidden md:grid md:grid-cols-2 gap-6 mb-8 max-w-4xl mx-auto">
          {instagramPosts.slice(0, 4).map((postUrl, index) => (
            <InstagramEmbed key={index} postUrl={postUrl} index={index} />
          ))}
        </div>

        {/* Mobile: Horizontal Scroll 1x4 */}
        <div className="md:hidden mb-8">
          <div className="flex gap-4 overflow-x-auto pb-4 px-4 -mx-4 snap-x snap-mandatory">
            {instagramPosts.slice(0, 4).map((postUrl, index) => (
              <div key={index} className="flex-none w-80 snap-start">
                <InstagramEmbed postUrl={postUrl} index={index} />
              </div>
            ))}
          </div>
          {/* Scroll indicator */}
          <div className="text-center text-gray-500 text-sm mt-2">
            ← Swipe to see more posts →
          </div>
        </div>

        {/* Follow Us Button */}
        <div className="text-center">
          <Button 
            className="bg-gradient-to-r from-primary to-orange hover:from-primary/90 hover:to-orange/90 text-white font-semibold px-8 py-3 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            onClick={() => window.open('https://www.instagram.com/shoppersdiscountclub?igsh=MWZvczJxenFuem15OA==', '_blank')}
          >
            <Instagram className="h-5 w-5 mr-2" />
            Follow Us @shoppersdiscountclub
          </Button>
        </div>

        {/* Update Instructions - Only show if no real posts are configured */}
        {instagramPosts.some(url => url.includes('PLACEHOLDER')) && (
          <div className="mt-8 p-4 bg-orange-50 rounded-lg border-l-4 border-orange-400">
            <h3 className="font-semibold text-charcoal mb-2">
              🔔 Setup Required: Add Your Instagram Posts
            </h3>
            <div className="text-sm text-gray-700 space-y-2">
              <p><strong>Your Instagram integration is ready! Next steps:</strong></p>
              <ol className="list-decimal list-inside space-y-1 ml-4">
                <li>Login to Admin Dashboard → Instagram tab</li>
                <li>Visit instagram.com/shoppersdiscountclub</li>
                <li>Copy URLs of your 4 best recent posts</li>
                <li>Paste them in the admin interface and save</li>
              </ol>
              <p className="text-xs text-orange-600 mt-2">
                Once configured, this message will disappear and your posts will display automatically.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Custom CSS for Instagram embeds */}
      <style jsx>{`
        .instagram-embed-container iframe {
          border-radius: 8px !important;
        }
        
        /* Responsive adjustments for Instagram embeds */
        @media (max-width: 768px) {
          .instagram-media {
            min-width: 300px !important;
          }
        }
        
        /* Custom scrollbar for mobile */
        .overflow-x-auto::-webkit-scrollbar {
          height: 4px;
        }
        
        .overflow-x-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 2px;
        }
        
        .overflow-x-auto::-webkit-scrollbar-thumb {
          background: #22c55e;
          border-radius: 2px;
        }
        
        .overflow-x-auto::-webkit-scrollbar-thumb:hover {
          background: #16a34a;
        }
        
        /* Smooth snap scrolling */
        .snap-x {
          scroll-behavior: smooth;
        }
      `}</style>
    </section>
  );
};

export default InstagramFeed;